import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createEventPaymentIntent, confirmEventPayment, getPaymentMethods, validateCoupon, getAssociateMembers, getCreditBalance } from '../../services/api';

// Load Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

const defaultBillingDetails = {
  name: '',
  email: '',
  address: {
    line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US'
  }
};

const EventPaymentFormContent = ({
  event,
  amount,
  onSuccess,
  onError,
  onClose,
  initialBilling
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [useNewCard, setUseNewCard] = useState(false);

  // Attendee selection state
  const [associateMembers, setAssociateMembers] = useState([]);
  const [selectedAttendee, setSelectedAttendee] = useState('self'); // 'self' or associate member ID

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [discountedAmount, setDiscountedAmount] = useState(amount);
  const [discountDetails, setDiscountDetails] = useState(null);

  // Credit balance state
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditsToApply, setCreditsToApply] = useState(0);

  const [billingDetails, setBillingDetails] = useState(() => {
    const base = {
      name: defaultBillingDetails.name,
      email: defaultBillingDetails.email,
      address: { ...defaultBillingDetails.address }
    };

    if (!initialBilling) {
      return base;
    }

    return {
      name: initialBilling.name || '',
      email: initialBilling.email || '',
      address: {
        line1: initialBilling.address?.line1 || '',
        city: initialBilling.address?.city || '',
        state: initialBilling.address?.state || '',
        postal_code: initialBilling.address?.postal_code || '',
        country: initialBilling.address?.country || 'US'
      }
    };
  });

  useEffect(() => {
    const loadPaymentMethods = async () => {
      try {
        const data = await getPaymentMethods();
        const methods = data.payment_methods || [];
        setPaymentMethods(methods);

        // Auto-select default payment method if exists
        const defaultMethod = methods.find(m => m.is_default);
        if (defaultMethod) {
          setSelectedPaymentMethod(defaultMethod.id);
        } else if (methods.length > 0) {
          setSelectedPaymentMethod(methods[0].id);
        } else {
          // No saved payment methods, show new card form
          setUseNewCard(true);
        }
      } catch (err) {
        console.error('Failed to load payment methods:', err);
        // If fails, show new card form
        setUseNewCard(true);
      }
    };

    loadPaymentMethods();
  }, []);

  useEffect(() => {
    const loadAssociateMembers = async () => {
      try {
        const data = await getAssociateMembers();
        const members = data.results || data;
        setAssociateMembers(members);
      } catch (err) {
        console.error('Failed to load associate members:', err);
        // Continue without associate members if fails
      }
    };

    loadAssociateMembers();
  }, []);

  useEffect(() => {
    const loadCreditBalance = async () => {
      try {
        const data = await getCreditBalance();
        setCreditBalance(data.balance || 0);
      } catch (err) {
        console.error('Failed to load credit balance:', err);
        setCreditBalance(0);
      }
    };

    loadCreditBalance();
  }, []);

  // Calculate credits to apply whenever amount changes
  useEffect(() => {
    const currentAmount = discountedAmount || amount;
    const creditsAvailable = Math.min(creditBalance, currentAmount);
    setCreditsToApply(creditsAvailable);
  }, [creditBalance, discountedAmount, amount]);

  const buildBillingMetadata = () => ({
    name: billingDetails.name,
    email: billingDetails.email,
    address_line1: billingDetails.address.line1,
    city: billingDetails.address.city,
    state: billingDetails.address.state,
    postal_code: billingDetails.address.postal_code,
    country: billingDetails.address.country
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalAmount = Math.max(0, (discountDetails?.final_amount || amount) - creditsToApply);

    // If credits cover the full amount, no payment needed
    if (finalAmount === 0) {
      setLoading(true);
      setError('');
      setProcessing(true);

      try {
        const intentData = await createEventPaymentIntent({
          event_id: event.id,
          associate_member_id: selectedAttendee !== 'self' ? selectedAttendee : null,
          coupon_code: couponApplied ? couponCode : null,
          billing_metadata: buildBillingMetadata()
        });

        if (intentData.free_event) {
          onSuccess({ success: true, registration: intentData.registration });
        } else {
          setError('Unexpected response from server. Please try again.');
          if (onError) {
            onError('Unexpected response from server.');
          }
        }
      } catch (err) {
        setError(err.message || 'An error occurred during registration.');
        if (onError) {
          onError(err.message);
        }
      } finally {
        setLoading(false);
        setProcessing(false);
      }
      return;
    }

    if (!stripe) {
      return;
    }

    // If using new card, validate elements are ready
    if (useNewCard && !elements) {
      return;
    }

    setLoading(true);
    setError('');
    setProcessing(true);

    try {
      // If using saved payment method
      if (!useNewCard && selectedPaymentMethod) {
        const intentData = await createEventPaymentIntent({
          event_id: event.id,
          payment_method_id: selectedPaymentMethod,
          associate_member_id: selectedAttendee !== 'self' ? selectedAttendee : null,
          coupon_code: couponApplied ? couponCode : null,
          billing_metadata: buildBillingMetadata()
        });

        // If credits covered the full amount, backend returns free_event
        if (intentData.free_event) {
          onSuccess({ success: true, registration: intentData.registration });
        } else if (intentData.payment_confirmed) {
          // Payment was confirmed on backend (using saved method)
          const confirmResult = await confirmEventPayment({
            payment_intent_id: intentData.payment_intent_id,
            payment_id: intentData.payment_id,
            event_id: event.id
          });

          if (confirmResult.success) {
            onSuccess(confirmResult);
          } else {
            setError('Payment confirmation failed. Please contact support.');
            if (onError) {
              onError('Payment confirmation failed. Please contact support.');
            }
          }
        } else {
          setError('Payment failed. Please try again or use a different payment method.');
          if (onError) {
            onError('Payment failed. Please try again.');
          }
        }
      } else {
        // Using new card - need to collect payment details
        const intentData = await createEventPaymentIntent({
          event_id: event.id,
          associate_member_id: selectedAttendee !== 'self' ? selectedAttendee : null,
          coupon_code: couponApplied ? couponCode : null,
          billing_metadata: buildBillingMetadata()
        });

        // If credits covered the full amount, backend returns free_event
        if (intentData.free_event) {
          onSuccess({ success: true, registration: intentData.registration });
        } else if (intentData.client_secret && intentData.payment_id) {
          // Confirm payment with Stripe
          const result = await stripe.confirmCardPayment(intentData.client_secret, {
            payment_method: {
              card: elements.getElement(CardNumberElement),
              billing_details: {
                name: billingDetails.name,
                email: billingDetails.email,
                address: billingDetails.address,
              },
            },
          });

          if (result.error) {
            setError(result.error.message);
            setProcessing(false);
            if (onError) {
              onError(result.error.message);
            }
          } else {
            // Payment succeeded, confirm with backend
            const confirmResult = await confirmEventPayment({
              payment_intent_id: result.paymentIntent.id,
              payment_id: intentData.payment_id,
              event_id: event.id
            });

            if (confirmResult.success) {
              onSuccess(confirmResult);
            } else {
              setError('Payment confirmation failed. Please contact support.');
              if (onError) {
                onError('Payment confirmation failed. Please contact support.');
              }
            }
          }
        } else {
          setError('Failed to create payment. Please try again.');
          if (onError) {
            onError('Failed to create payment.');
          }
        }
      }
    } catch (err) {
      setError(err.message || 'An error occurred during payment processing.');
      if (onError) {
        onError(err.message);
      }
    } finally {
      setLoading(false);
      setProcessing(false);
    }
  };

  const cardStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBillingDetails(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setBillingDetails(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handle coupon validation
  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setCouponValidating(true);
    setCouponError('');

    try {
      const result = await validateCoupon(couponCode, event.id, amount);

      if (result.valid) {
        setCouponApplied(true);
        setDiscountedAmount(result.pricing.final_amount);
        setDiscountDetails(result.pricing);
        setCouponError('');
      } else {
        setCouponError(result.error || 'Invalid coupon code');
        setCouponApplied(false);
      }
    } catch (error) {
      setCouponError(error.message || 'Failed to validate coupon');
      setCouponApplied(false);
    } finally {
      setCouponValidating(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponApplied(false);
    setDiscountedAmount(amount);
    setDiscountDetails(null);
    setCouponError('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Event Registration Payment</h3>
        <div className="bg-gray-50 rounded-md p-4 mb-4">
          <p className="text-gray-700 font-medium">{event.name}</p>
          <p className="text-gray-600 text-sm mt-1">
            {new Date(event.start_date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>
      </div>

      {/* Attendee Selection */}
      {associateMembers.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Who's attending?
          </label>
          <select
            value={selectedAttendee}
            onChange={(e) => setSelectedAttendee(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="self">Myself</option>
            {associateMembers.map((member) => (
              <option key={member.id} value={member.id}>
                {member.full_name} ({member.relationship})
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Select who will be attending this event
          </p>
        </div>
      )}

      {/* Coupon Code Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Coupon Code (Optional)
        </label>

        {!couponApplied ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Enter coupon code"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={couponValidating}
            />
            <button
              type="button"
              onClick={handleValidateCoupon}
              disabled={couponValidating || !couponCode.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {couponValidating ? 'Validating...' : 'Apply'}
            </button>
          </div>
        ) : (
          <div className="p-3 bg-green-50 border border-green-200 rounded-md flex justify-between items-center">
            <div>
              <span className="text-green-700 font-medium">{couponCode}</span>
              <span className="text-sm text-gray-600 ml-2">applied!</span>
            </div>
            <button
              type="button"
              onClick={handleRemoveCoupon}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Remove
            </button>
          </div>
        )}

        {couponError && (
          <p className="mt-1 text-sm text-red-600">{couponError}</p>
        )}
      </div>

      {/* Price Summary */}
      <div className="p-4 bg-gray-50 rounded-md">
        {discountDetails || creditsToApply > 0 ? (
          <>
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Original Price:</span>
              <span className={discountDetails ? "line-through" : ""}>${Number(amount).toFixed(2)}</span>
            </div>
            {discountDetails && (
              <div className="flex justify-between text-sm text-green-600 mb-1">
                <span>Coupon Discount:</span>
                <span>-${discountDetails.discount_amount.toFixed(2)}</span>
              </div>
            )}
            {creditsToApply > 0 && (
              <div className="flex justify-between text-sm text-emerald-600 mb-1">
                <span>Credits Applied:</span>
                <span>-${creditsToApply.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
              <span>Amount to Pay:</span>
              <span>${Math.max(0, (discountDetails?.final_amount || amount) - creditsToApply).toFixed(2)}</span>
            </div>
            {creditsToApply > 0 && Math.max(0, (discountDetails?.final_amount || amount) - creditsToApply) === 0 && (
              <p className="text-sm text-green-600 mt-2">✓ This event is free with your credits!</p>
            )}
          </>
        ) : (
          <div className="flex justify-between font-bold text-lg">
            <span>Total:</span>
            <span>${Number(amount).toFixed(2)}</span>
          </div>
        )}
        {creditBalance > 0 && creditsToApply === 0 && (
          <p className="text-sm text-gray-600 mt-2">
            You have ${creditBalance.toFixed(2)} in credits available
          </p>
        )}
      </div>

      <div className="space-y-6">
        {/* Payment Method Selection */}
        {paymentMethods.length > 0 && Math.max(0, (discountDetails?.final_amount || amount) - creditsToApply) > 0 && (
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Payment Method</h4>
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <label
                  key={method.id}
                  className={`flex items-center p-3 border rounded-md cursor-pointer ${
                    !useNewCard && selectedPaymentMethod === method.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment_method"
                    value={method.id}
                    checked={!useNewCard && selectedPaymentMethod === method.id}
                    onChange={() => {
                      setSelectedPaymentMethod(method.id);
                      setUseNewCard(false);
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-3 flex items-center flex-1">
                    <div className="flex-1">
                      <span className="text-gray-900 font-medium capitalize">
                        {method.card.brand}
                      </span>
                      <span className="text-gray-600 ml-2">
                        •••• {method.card.last4}
                      </span>
                      <span className="text-gray-500 text-sm ml-2">
                        Exp: {method.card.exp_month}/{method.card.exp_year}
                      </span>
                    </div>
                    {method.is_default && (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded">
                        Default
                      </span>
                    )}
                  </div>
                </label>
              ))}

              <label
                className={`flex items-center p-3 border rounded-md cursor-pointer ${
                  useNewCard
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="payment_method"
                  value="new"
                  checked={useNewCard}
                  onChange={() => setUseNewCard(true)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-3 text-gray-900 font-medium">
                  Use a new card
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Billing Information */}
        {useNewCard && (
          <>
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Billing Information</h4>
              <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input
                type="text"
                value={billingDetails.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                value={billingDetails.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="your.email@domain.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Billing Address</label>
              <input
                type="text"
                value={billingDetails.address.line1}
                onChange={(e) => handleInputChange('address.line1', e.target.value)}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="123 Main Street"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  value={billingDetails.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="New York"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <input
                  type="text"
                  value={billingDetails.address.state}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="NY"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
              <input
                type="text"
                value={billingDetails.address.postal_code}
                onChange={(e) => handleInputChange('address.postal_code', e.target.value)}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="10001"
                required
              />
            </div>
              </div>
            </div>

            {/* Payment Information */}
            <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Payment Information</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Card Number</label>
                <div className="border border-gray-300 rounded-md p-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <CardNumberElement options={cardStyle} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expiration Date</label>
                  <div className="border border-gray-300 rounded-md p-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                    <CardExpiryElement options={cardStyle} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Security Code</label>
                  <div className="border border-gray-300 rounded-md p-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                    <CardCvcElement options={cardStyle} />
                  </div>
                </div>
              </div>
            </div>
            </div>
          </>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {useNewCard && (
          <p className="text-sm text-gray-500 text-center">
            * All fields are required
          </p>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 rounded-md shadow-sm text-lg font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              loading || processing ||
              (Math.max(0, (discountDetails?.final_amount || amount) - creditsToApply) > 0 &&
                (!stripe || (!useNewCard && !selectedPaymentMethod)))
            }
            className="px-6 py-3 border border-transparent rounded-md shadow-sm text-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {processing
              ? 'Processing...'
              : Math.max(0, (discountDetails?.final_amount || amount) - creditsToApply) === 0
                ? 'Complete Registration'
                : 'Pay Now'}
          </button>
        </div>
      </div>
    </form>
  );
};

const EventPaymentForm = ({
  event,
  amount,
  onSuccess,
  onError,
  onClose,
  initialBilling
}) => {
  return (
    <Elements stripe={stripePromise}>
      <EventPaymentFormContent
        event={event}
        amount={amount}
        onSuccess={onSuccess}
        onError={onError}
        onClose={onClose}
        initialBilling={initialBilling}
      />
    </Elements>
  );
};

export default EventPaymentForm;
